import {useEffect, useMemo, useState} from 'react';
import {FabricObject} from 'fabric';
import {Autocomplete, Box, IconButton, Stack, Switch, TextField, Tooltip, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from 'react-query';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CNumberField from 'crqlar-react-common/dist/components/CNumberField';
import {useFloorplanContext} from '../hooks/useFloorplanContext';
import {getTableCapacity} from '../utils/fabric-factory';
import {useServicesForSector} from 'crqlar-react-common/dist/utils/hooks/services/useServices';
import {Service} from 'crqlar-react-common/dist/models/service/service.model';
import {useAllSectors} from 'crqlar-react-common/dist/utils/hooks/sectors/useSectors';
import {SectorType, SerialReservationAutomationSettings} from 'crqlar-react-common/dist/models/sector/sector.model';
import {SectorService} from 'crqlar-react-common/dist/services/sector/sector.service';

const InfoTooltip = ({title}: { title: string }) => (
    <Tooltip
        title={title}
        placement="right"
        componentsProps={{tooltip: {sx: {fontSize: '0.85rem'}}}}
    >
        <InfoOutlinedIcon
            sx={{color: 'info.main', cursor: 'help', ml: 0.5, fontSize: '1.2rem', verticalAlign: 'middle'}}/>
    </Tooltip>
);

const SettingRow = ({
                        id,
                        checked,
                        disabled,
                        label,
                        onChange,
                        tooltip,
                    }: {
    id?: string;
    checked: boolean;
    disabled?: boolean;
    label: string;
    onChange: (checked: boolean) => void;
    tooltip?: string;
}) => (
    <Stack direction="row" alignItems="center" spacing={1.5}>
        <Switch
            id={id}
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
        />
        <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.25}}>
            <Typography variant="body2" sx={{lineHeight: 1.3}}>
                {label}
            </Typography>
            {tooltip ? <InfoTooltip title={tooltip}/> : null}
        </Box>
    </Stack>
);

interface Props {
    elements: FabricObject[];
    onElementUpdated: () => void;
}

const EditSidebarGeneral: React.FC<Props> = ({
    elements,
    onElementUpdated,
}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {restaurantDraft, setRestaurantDraft, updateAllTableElementsLocally} = useFloorplanContext();
    const restaurant = restaurantDraft;
    const allSectors = useAllSectors();
    const accommodationSector = allSectors.find(s => s.sectorType === SectorType.ACCOMMODATION);
    const allServices = useServicesForSector(accommodationSector?.id);

    // The serial-reservation automation lives on the restaurant Sector (same object the
    // system > sectors page edits), not on the restaurant Resource this floorplan edits.
    // Prefer the sector this restaurant is linked to, falling back to the org's restaurant sector.
    const restaurantSector = allSectors.find(
        s => s.sectorType === SectorType.RESTAURANT && (restaurant?.sectorIds ?? []).includes(s.id!),
    ) ?? allSectors.find(s => s.sectorType === SectorType.RESTAURANT);
    const halfboardServiceIds = (restaurant?.meta?.halfboardServiceIds as string[] | undefined)
        ?? (restaurant?.meta?.serviceIds as string[] | undefined)
        ?? [];
    const selectedServices = allServices.filter(s => halfboardServiceIds.includes(s.id!));

    // In edit mode, derive the total from live canvas tables so unsaved seat changes are included.
    const childrenTotalCapacity = useMemo(
        () => elements.reduce((acc, element) => {
            const table = element.floorPlanItem?.table;
            const tableCapacity = table ? getTableCapacity(table.seats, table.resource?.capacity ?? 0) : 0;
            return acc + tableCapacity;
        }, 0),
        [elements],
    );
    const maxSeats = restaurant?.calculateCapacityFromChildren ? childrenTotalCapacity : (restaurant?.capacity ?? 0);
    const allTablesBookable = useMemo(
        () => {
            const tableElements = elements.filter((element) => !!element.floorPlanItem?.table);
            return tableElements.length > 0
                && tableElements.every((element) => element.floorPlanItem?.table?.resource?.meta?.bookableExternally === true);
        },
        [elements],
    );

    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedCapacity, setEditedCapacity] = useState(0);
    // Local mirror of the sector's automation settings so toggles feel instant (optimistic).
    const [automation, setAutomation] = useState<SerialReservationAutomationSettings>({});
    const [savingAutomation, setSavingAutomation] = useState(false);

    useEffect(() => {
        setEditedCapacity(restaurant?.capacity ?? 0);
    }, [restaurant?.capacity]);

    useEffect(() => {
        setAutomation(restaurantSector?.serialReservationAutomation ?? {});
    }, [restaurantSector?.id, restaurantSector?.serialReservationAutomation]);

    const handleStartEditing = () => {
        setEditedName(restaurant?.name || '');
        setIsEditingName(true);
    };

    const handleCancelEditing = () => {
        setIsEditingName(false);
    };

    const handleSaveName = () => {
        if (!restaurant || !editedName.trim()) return;
        setRestaurantDraft(prev => prev ? ({
            ...prev,
            name: editedName.trim(),
        }) : prev);
        setIsEditingName(false);
    };

    const handleSaveCapacity = (nextCapacity: number) => {
        if (!restaurant || restaurant.calculateCapacityFromChildren) return;

        if (nextCapacity < 0) {
            setEditedCapacity(restaurant.capacity ?? 0);
            return;
        }

        if (nextCapacity === (restaurant.capacity ?? 0)) return;

        setRestaurantDraft(prev => prev ? ({
            ...prev,
            capacity: nextCapacity,
        }) : prev);
    };

    const isCalculatedCapacity = restaurant?.calculateCapacityFromChildren ?? false;

    const handleToggleSeatsAroundTable = (checked: boolean) => {
        if (!restaurant) return;
        setRestaurantDraft(prev => prev ? ({
            ...prev,
            meta: {
                ...prev.meta,
                seatsAroundTable: checked,
            },
        }) : prev);
    };

    const handleToggleFloorplanReservationTooltip = (checked: boolean) => {
        if (!restaurant) return;
        setRestaurantDraft(prev => prev ? ({
            ...prev,
            meta: {
                ...prev.meta,
                floorplanReservationTooltipEnabled: checked,
            },
        }) : prev);
    };

    // Manual capacity is only valid while the restaurant is not syncing from child tables.
    const handleToggleCapacityFromChildren = (checked: boolean) => {
        if (!restaurant) return;
        setRestaurantDraft(prev => prev ? ({
            ...prev,
            calculateCapacityFromChildren: checked,
        }) : prev);
    };

    const handleToggleAutoAssign = (checked: boolean) => {
        if (!restaurant) return;
        setRestaurantDraft(prev => prev ? ({
            ...prev,
            meta: {
                ...prev.meta,
                autoAssignExternally: checked,
            },
        }) : prev);

        const updatedTables = updateAllTableElementsLocally((item) => {
            if (!item.table) return item;

            return {
                ...item,
                table: {
                    ...item.table,
                    resource: {
                        ...item.table.resource,
                        meta: {
                            ...item.table.resource?.meta,
                            autoAssignExternally: checked,
                        },
                    },
                },
            };
        });
        if (updatedTables.length > 0) {
            onElementUpdated();
        }
    };

    const handleToggleMakeAllBookable = (checked: boolean) => {
        const updatedTables = updateAllTableElementsLocally((item) => {
            if (!item.table) return item;

            return {
                ...item,
                table: {
                    ...item.table,
                    resource: {
                        ...item.table.resource,
                        meta: {
                            ...item.table.resource?.meta,
                            bookableExternally: checked,
                        },
                    },
                },
            };
        });
        if (updatedTables.length > 0) {
            onElementUpdated();
        }
    };

    // Persists directly to the restaurant Sector so the switch mirrors the one on the
    // system > sectors page (rather than the floorplan's own Save flow, which saves the Resource).
    const updateAutomationSetting = async (
        field: keyof SerialReservationAutomationSettings,
        value: boolean,
    ) => {
        if (!restaurantSector || savingAutomation) return;

        const previous = automation;
        const next = {...automation, [field]: value};
        setAutomation(next); // optimistic
        setSavingAutomation(true);
        try {
            await SectorService.updateSector({
                ...restaurantSector,
                serialReservationAutomation: next,
            });
            await queryClient.invalidateQueries('allSectors');
        } catch (error) {
            console.error('Failed to update restaurant automation setting:', error);
            setAutomation(previous); // revert on failure
        } finally {
            setSavingAutomation(false);
        }
    };

    return (
        <Box sx={{flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
            {/* Restaurant Name */}
            <Box>
                {isEditingName ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <TextField
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            size="small"
                            autoFocus
                            fullWidth
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveName();
                                if (e.key === 'Escape') handleCancelEditing();
                            }}
                        />
                        <IconButton size="small" onClick={handleSaveName}>
                            <CheckIcon fontSize="small"/>
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEditing}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Stack>
                ) : (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight="bold">
                            {restaurant?.name || '-'}
                        </Typography>
                        <IconButton size="small" onClick={handleStartEditing}>
                            <EditIcon fontSize="small"/>
                        </IconButton>
                    </Stack>
                )}
            </Box>

            {/* Max. Seats */}
            <Box>
                <Stack direction="row" alignItems="center">
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('Max. Seats')}
                    </Typography>
                    <InfoTooltip
                        title={t('Capacity means the number of seats on a table, the amount of people that can be serviced at the same time by the staff member or in the room/facility.')}/>
                </Stack>
                <CNumberField
                    value={isCalculatedCapacity ? maxSeats : editedCapacity}
                    onChange={(value) => {
                        const normalizedValue = value ?? 0;
                        setEditedCapacity(normalizedValue);
                        handleSaveCapacity(normalizedValue);
                    }}
                    size="small"
                    fullWidth
                    disabled={isCalculatedCapacity}
                    min={0}
                    sx={{mt: 0.5}}
                />
            </Box>

         <Box>
             <SettingRow
                 checked={restaurant?.calculateCapacityFromChildren ?? false}
                 onChange={handleToggleCapacityFromChildren}
                 label={t('Sync with table list (sum of all seats)')}
                 tooltip={t('Total capacity is the sum of all table capacities')}
             />

             <SettingRow
                 checked={restaurant?.meta?.autoAssignExternally ?? false}
                 onChange={handleToggleAutoAssign}
                 label={t('Automatic Table Allocation (Online Bookings)')}
                 tooltip={t('Automatically assign the table during a widget booking')}
             />

             <SettingRow
                 checked={allTablesBookable}
                 onChange={handleToggleMakeAllBookable}
                 label={t('Make all tables bookable through widget')}
             />

             <SettingRow
                 checked={restaurant?.meta?.seatsAroundTable ?? false}
                 onChange={handleToggleSeatsAroundTable}
                 label={t('Show seats around tables in floorplan')}
             />

             <SettingRow
                 checked={restaurant?.meta?.floorplanReservationTooltipEnabled !== false}
                 onChange={handleToggleFloorplanReservationTooltip}
                 label={t('Show reservation tooltip on floorplan')}
                 tooltip={t('Show reservation details when hovering over a table on the floorplan')}
             />
         </Box>

            {/* Room Codes for Halfboard */}
            <Box>
                <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5}}>
                    {t('Room Codes for Halfboard')}
                </Typography>
                <Autocomplete<Service, true>
                    multiple
                    options={allServices}
                    getOptionLabel={(option) => option.name || ''}
                    value={selectedServices}
                    onChange={(_event, newValue) => {
                        setRestaurantDraft(prev => prev ? ({
                            ...prev,
                            meta: {
                                ...prev.meta,
                                halfboardServiceIds: newValue.map(s => s.id),
                            },
                        }) : prev);
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={t('Select services')}
                        />
                    )}
                />
            </Box>

            {restaurantSector && (
                <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5}}>
                        {t('Automation')}
                    </Typography>
                    <Box>
                        <SettingRow
                            id="auto-cancel-early-departure"
                            checked={!!automation.autoCancelOnEarlyDeparture}
                            disabled={savingAutomation}
                            onChange={(checked) => updateAutomationSetting('autoCancelOnEarlyDeparture', checked)}
                            label={t('Auto-cancel series on early departure')}
                            tooltip={t('When a guest departs early, automatically cancel their serial restaurant reservations that fall after the new departure date.')}
                        />

                        <SettingRow
                            id="auto-create-prolonged-stay"
                            checked={!!automation.autoCreateOnProlongedStay}
                            disabled={savingAutomation}
                            onChange={(checked) => updateAutomationSetting('autoCreateOnProlongedStay', checked)}
                            label={t('Auto-create series on prolonged stay')}
                            tooltip={t('When a guest extends their stay, automatically create the restaurant bookings for the newly-covered days, matching their existing meal-series pattern.')}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default EditSidebarGeneral;
